import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Button from '../../../../components/button/button';
import { useInvoiceContext } from '../../../../utils/invoiceContext';
import { useCustomerContext } from '../../../../utils/customerContext';
import Label from '../../../../components/label/label';
import Input from '../../../../components/input/input';
import DatePicker from 'react-datepicker';
import CustomSelect from '../../../../components/customSelect/customSelect';
import "react-datepicker/dist/react-datepicker.css";
import '../components/invoice.component.css';

function EditInvoice({ onCancel }) {
    const [innerInvoiceItems, setInnerInvoiceItems] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [actionDate, setActionDate] = useState(new Date());
    const [actionDateTo, setActionDateTo] = useState(null);
    const [isDateRange, setIsDateRange] = useState(false);
    const [invoiceDate, setInvoiceDate] = useState(new Date());
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const { id } = useParams();
    const { fetchOneInvoice, invoiceData, invoiceItems, saveEditedInvoice } = useInvoiceContext();
    const { fetchCustomers, customers } = useCustomerContext();

    const customerItems = customers.map(c => ({
        _id: c._id,
        name: `${c.prename || ''} ${c.name || ''}`.trim(),
        customerNumber: c.customerNumber
    }));

    useEffect(() => {
        fetchOneInvoice(id);
        fetchCustomers();
    }, [id]);

    useEffect(() => {
        if (invoiceData && !isLoaded) {
            if (invoiceData.actionDate) setActionDate(new Date(invoiceData.actionDate));
            if (invoiceData.invoiceDate) setInvoiceDate(new Date(invoiceData.invoiceDate));

            if (invoiceData.actionDateTo) {
                setActionDateTo(new Date(invoiceData.actionDateTo));
                setIsDateRange(true);
            }

            if (invoiceData.customer) {
                setSelectedCustomer({
                    _id: invoiceData.customer._id,
                    name: `${invoiceData.customer.prename || ''} ${invoiceData.customer.name || ''}`.trim(),
                    customerNumber: invoiceData.customer.customerNumber
                });
            }

            if (invoiceData.isPaid !== undefined) {
                setIsPaid(invoiceData.isPaid);
                setSelectedPaid(invoiceData.isPaid ? paidItems[0] : paidItems[1]);
            }

            if (invoiceData.paymentMethod) {
                const paymentItem = paymentItems.find(item => item.id === invoiceData.paymentMethod);
                if (paymentItem) {
                    setSelectedPayment(paymentItem);
                    setPaymentMethod(invoiceData.paymentMethod);
                    if (invoiceData.paymentMethod === 'Sonstige') {
                        setCustomPaymentMethod(invoiceData.paymentMethod);
                    }
                }
            }

            if (invoiceItems && invoiceItems.length > 0) {
                setInnerInvoiceItems(recalculateItems(invoiceItems, invoiceData.tax));
                setIsLoaded(true);
            }
        }
    }, [invoiceItems, invoiceData, isLoaded]);

    const recalculateItems = (items, tax = 0) => {
        return items.map(item => {
            const totalNet = (Number(item.price) || 0) * (Number(item.count) || 0);
            const totalBrut = totalNet + (totalNet * (Number(tax) || 0) / 100);
            return { ...item, totalNet, totalBrut };
        });
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = innerInvoiceItems.map((item, idx) =>
            idx === index ? { ...item, [field]: value } : item
        );
        setInnerInvoiceItems(recalculateItems(updatedItems, invoiceData.tax));
    };

    const totalNet = innerInvoiceItems.reduce((sum, item) => sum + (item.totalNet || 0), 0);
    const totalBrut = innerInvoiceItems.reduce((sum, item) => sum + (item.totalBrut || 0), 0);

    const handleRemove = (index) => {
        const updatedItems = innerInvoiceItems.filter((_, i) => i !== index);
        setInnerInvoiceItems(updatedItems);
    };

    const handleAddRow = () => {
        const nextPosition = innerInvoiceItems.length > 0
            ? Math.max(...innerInvoiceItems.map(item => item.position)) + 1
            : 1;
        const newItem = { position: nextPosition, item: '', price: 0, count: 0, totalNet: 0, totalBrut: 0 };
        setInnerInvoiceItems(recalculateItems([...innerInvoiceItems, newItem], invoiceData.tax));
    };

    const handleSave = () => {
        saveEditedInvoice(
            id,
            innerInvoiceItems,
            totalNet,
            totalBrut,
            actionDate,
            actionDateTo,
            invoiceDate,
            selectedCustomer?._id,
            paymentMethod
        );
    };

    const paidItems = [{id: 'true', name: 'Ja'}, {id: 'false', name: 'Nein'}];
    const paymentItems = [{id: 'Barzahlung', name: 'Barzahlung'}, {id: 'Überweisung', name: 'Überweisung'}, {id: 'Kreditkarte', name: 'Kreditkarte'}, {id: 'Paypal', name: 'Paypal'}, {id: 'Sonstige', name: 'Sonstige'}];
    const [selectedPaid, setSelectedPaid] = useState(paidItems[1]);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [customPaymentMethod, setCustomPaymentMethod] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [isPaid, setIsPaid] = useState(false);

    return (
        <div>
            <div style={{ margin: '20px 0' }}>
                <Label labelValue='Kunde: ' />
                <CustomSelect
                    items={customerItems}
                    selected={selectedCustomer}
                    setSelected={setSelectedCustomer}
                    placeholder="Kunden auswählen"
                    search={true}
                />
            </div>

            <div style={{ margin: '20px 0' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Label labelValue='Leistungsdatum' />
                    <button
                        type="button"
                        onClick={() => {
                            setIsDateRange(!isDateRange);
                            setActionDateTo(null);
                        }}
                        style={{ fontSize: '12px', cursor: 'pointer' }}
                    >
                        {isDateRange ? '→ Einzeldatum' : '→ Zeitraum'}
                    </button>
                </div>

                {!isDateRange ? (
                    <DatePicker
                        selected={actionDate}
                        onChange={date => setActionDate(date)}
                        dateFormat="dd.MM.yyyy"
                        todayButton="Heute"
                        className='datepickerInput'
                    />
                ) : (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <DatePicker
                            selected={actionDate}
                            onChange={date => setActionDate(date)}
                            dateFormat="dd.MM.yyyy"
                            todayButton="Heute"
                            className='datepickerInput'
                            selectsStart
                            startDate={actionDate}
                            endDate={actionDateTo}
                        />
                        <span>bis</span>
                        <DatePicker
                            selected={actionDateTo}
                            onChange={date => setActionDateTo(date)}
                            dateFormat="dd.MM.yyyy"
                            className='datepickerInput'
                            selectsEnd
                            startDate={actionDate}
                            endDate={actionDateTo}
                            minDate={actionDate}
                        />
                    </div>
                )}

                <br />
                <Label labelValue='Rechnungsdatum: ' />
                <DatePicker
                    selected={invoiceDate}
                    onChange={date => setInvoiceDate(date)}
                    dateFormat="dd.MM.yyyy"
                    todayButton="Heute"
                    className='datepickerInput'
                />
            </div>

            <div>
                <Label labelValue='Wie wurde bezahlt?' />
                <CustomSelect
                    items={paymentItems}
                    selected={selectedPayment}
                    setSelected={(item) => {
                        setSelectedPayment(item);
                        if (item?.id === 'Sonstige') {
                            setPaymentMethod(customPaymentMethod);
                        } else {
                            setPaymentMethod(item?.id || '');
                            setCustomPaymentMethod('');
                        }
                    }}
                    placeholder="Hier auswählen"
                    search={false}
                />
                {selectedPayment?.id === 'Sonstige' && (
                    <>
                        <Label labelFor='customPaymentMethod' labelValue='Andere Zahlungsmethode' />
                        <Input
                            inputId='customPaymentMethod'
                            inputName='customPaymentMethod'
                            inputType='text'
                            width='100%'
                            borderColor='black'
                            inputValue={customPaymentMethod}
                            onChange={(e) => {
                                setCustomPaymentMethod(e.target.value);
                                setPaymentMethod(e.target.value);
                            }}
                            placeholder='Zahlungsmethode eingeben'
                        />
                    </>
                )}
            </div>

            <table className='createTable'>
                <thead>
                    <tr>
                        <th className='createTh'>Pos.</th>
                        <th className='createTh'>Leistung</th>
                        <th className='createTh'>Einzelpreis<br />(Netto)</th>
                        <th className='createTh'>Anzahl</th>
                        <th className='createTh'>Entfernen</th>
                    </tr>
                </thead>
                <tbody>
                    {innerInvoiceItems.map((item, idx) => (
                        <tr key={item.position || idx}>
                            <td className='createTd' data-label='Position'>{item.position}</td>
                            <td className='createTd' id='createTdMobile'>
                                <Input className='editInput' inputType="text" fontSize='14px' borderColor='black' inputValue={item.item} onChange={e => handleItemChange(idx, 'item', e.target.value)} />
                                <span>Preis: <Input className='editInput' inputType="number" inputValue={item.price} fontSize='14px' borderColor='black' width='100px' onChange={e => handleItemChange(idx, 'price', e.target.value)} /></span>
                                <span>x</span>
                                <span>Stück: <Input className='editInput' inputType="number" inputValue={item.count} fontSize='14px' borderColor='black' width='100px' onChange={e => handleItemChange(idx, 'count', e.target.value)} /></span>
                                <span><strong>{item.totalNet.toFixed(2)} €</strong></span>
                            </td>
                            <td className='createTd' data-label='Leistung'>
                                <Input className='editInput' inputType="text" fontSize='14px' borderColor='black' inputValue={item.item} onChange={e => handleItemChange(idx, 'item', e.target.value)} />
                            </td>
                            <td className='createTd' data-label='Einzelpreis (Netto)'>
                                <Input className='editInput' inputType="number" inputValue={item.price} fontSize='14px' borderColor='black' width='100px' onChange={e => handleItemChange(idx, 'price', e.target.value)} />
                            </td>
                            <td className='createTd' data-label='Anzahl'>
                                <Input className='editInput' inputType="number" inputValue={item.count} fontSize='14px' borderColor='black' width='100px' onChange={e => handleItemChange(idx, 'count', e.target.value)} />
                            </td>
                            <td className='createTd' data-label='Entfernen' style={{ cursor: 'pointer', color: 'red', fontWeight: 'bold' }} onClick={() => handleRemove(idx)}>
                                X
                            </td>
                        </tr>
                    ))}
                    <tr>
                        <td colSpan="7" className='createTd' style={{ textAlign: 'right' }}>
                            <strong>Gesamtpreis Netto: {totalNet.toFixed(2)} €</strong>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="7" className='createTd' style={{ textAlign: 'right' }}>
                            <strong>Gesamtpreis Brutto: {totalBrut.toFixed(2)} €</strong>
                        </td>
                    </tr>
                </tbody>
            </table>

            <Button onClick={handleAddRow} buttonValue='Zeile hinzufügen' />
            <Button onClick={handleSave} buttonValue='Speichern' />
            <Button onClick={onCancel} buttonValue='Abbrechen' />
        </div>
    );
}

export default EditInvoice;